import { createContext, useState, useEffect } from "react";
import "@/styles/globals.css";
import "animate.css";
import "@/styles/input-passenger.css";
import App from "next/app";
import { wrapper } from "@/stores";
import { Provider } from "react-redux";
import { ThemeProvider } from "styled-components";
import { lightTheme, darkTheme, GlobalStyles } from "@/themes/ThemeConfig";
import { ConfigProvider } from "antd";
import { getUserInfo } from "@/stores/apis/apiAuth";
import { setAuthUserInfo } from "@/stores/slices/authSlice";
import NextNProgress from "nextjs-progressbar";
import Head from "next/head";
export const GlobalContext = createContext({});
import "../../public/antd.min.css";
import { getAircraftCategories } from "@/stores/apis/apiAircraft";
import { setAircraftCategories } from "@/stores/slices/settingSlice";
import ErrorBoundary from "@/components/ErrorBoundary";
import { HeaderHome } from "@/components/ui/Header";
import { HeaderSolidProvider } from "@/hooks/HeaderSolidContext";
import { sortedCategory } from "../utils";
import { getCookie } from "cookies-next";
import { SplashScreen } from "@/components/molecules/SplashScreen";
import Script from "next/script";
import { MetaPixel } from "@/components/molecules/MetaPixel";
import { captureTrafficSourceFromUrl } from "@/utils/trafficSource";
import { useRouter } from "next/router";
function LandingApp({ Component, ...rest }) {
	const router = useRouter();
	const { store, props = {} } = wrapper.useWrappedStore(rest);
	const [theme, setTheme] = useState("light");
	const [loadingSplash, setLoadingSplash] = useState(true);

	const fetchUserInfo = async () => {
		let userToken = getCookie("ACCESS_TOKEN");
		if (userToken) {
			let response = await getUserInfo();
			if (response && response.success) {
				store.dispatch(setAuthUserInfo(response.data));
			}
		}
	};

	const fetchAircraftCategories = async () => {
		if (props?.pageProps?.aircraft_categories?.length > 0) {
			store.dispatch(setAircraftCategories(props?.pageProps?.aircraft_categories));
		}
	};

	useEffect(() => {
		fetchUserInfo();
		fetchAircraftCategories();
		// capture the first time
		captureTrafficSourceFromUrl();

		// every time the route is changed, keep caturing
		const onRouteChangeComplete = () => captureTrafficSourceFromUrl();
		router.events.on("routeChangeComplete", onRouteChangeComplete);

		return () => {
			router.events.off("routeChangeComplete", onRouteChangeComplete);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			{/* Google Tag Manager */}
			<noscript>
				<iframe
					src="https://www.googletagmanager.com/ns.html?id=GTM-W8XP3FFB"
					height="0"
					width="0"
					style={{ display: "none", visibility: "hidden" }}
				></iframe>
			</noscript>
			<ErrorBoundary>
				<Provider store={store}>
					<ThemeProvider theme={theme == "light" ? lightTheme : darkTheme}>
						<GlobalStyles />
						<ConfigProvider
							theme={{
								hashed: false,
								token: {
									fontFamily: "DM Sans",
									colorPrimary: lightTheme.color_primary_500,
									colorPrimaryActive: lightTheme.color_primary_600,
									colorPrimaryBg: lightTheme.background_200,
									colorPrimaryBorder: lightTheme.color_primary_500,
									colorPrimaryBorderHover: lightTheme.color_primary_500,
									colorPrimaryHover: lightTheme.color_primary_500,
									colorPrimaryBgHover: lightTheme.background_300,
									colorPrimaryText: lightTheme.color_primary_500,
									colorPrimaryTextActive: lightTheme.color_primary_600,
									borderRadius: 12,
									borderRadiusLG: 8,
									borderRadiusSM: 6,
									borderRadiusXS: 4,
								},
								components: {
									Steps: {
										controlItemBgActive: lightTheme.color_primary_500,
									},
									InputNumber: {
										controlHeight: 44,
										controlHeightLG: 52,
										controlHeightSM: 36,
										borderRadius: 12,
										borderRadiusLG: 12,
									},
									Input: {
										controlHeight: 44,
										controlHeightLG: 52,
										controlHeightSM: 36,
										borderRadius: 12,
										borderRadiusLG: 12,
									},
								},
							}}
						>
							<Head>
								<meta
									name="viewport"
									content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
								/>
							</Head>
							<NextNProgress
								color="#FFB11A"
								options={{
									showSpinner: false,
								}}
							/>
							<HeaderSolidProvider>
								<HeaderHome />
								<MetaPixel />
								{loadingSplash && <SplashScreen onFinishLoading={() => setLoadingSplash(false)} />}
								<Component {...props.pageProps} />
							</HeaderSolidProvider>
						</ConfigProvider>
					</ThemeProvider>
				</Provider>
			</ErrorBoundary>
		</>
	);
}

// getInitialProps disables automatic static optimization for pages that don't
// have getStaticProps. So article, category and home pages still get SSG.
LandingApp.getInitialProps = async (ctx) => {
	// Calls page's `getInitialProps` and fills `appProps.pageProps`
	const appProps = await App.getInitialProps(ctx);

	let aircraft_categories = [];
	const aircrafts = await getAircraftCategories({ include_type: true });
	if (aircrafts?.success) {
		aircraft_categories = sortedCategory(aircrafts?.data?.docs || []);
	}

	// Pass the data to our page via props
	return { ...appProps, pageProps: { aircraft_categories } };
};

export default LandingApp;
